import { createHmac } from "node:crypto";
import type { ConnectorAccessProbeResult, ConnectorDefinition } from "../types";

const PROBE_TIMEOUT_MS = 8000;

/**
 * CxAlloy TQ REST API host. Single shared host for every CxAlloy account (the
 * key identifies the account), so there is no tenant domain to configure.
 * `CXALLOY_API_BASE` exists only as an override for testing. Exported so the
 * `@workspace/ai` CxAlloy client reuses the same base.
 */
export function cxalloyApiBase(): string {
  return (
    process.env.CXALLOY_API_BASE?.trim() || "https://tq.cxalloy.com/api/v1"
  );
}

export const CXALLOY_USER_AGENT = "magnus-cxalloy/1.0";

/**
 * CxAlloy request signature: HMAC-SHA256 (hex) keyed by the API key secret,
 * over the JSON body with the unix timestamp appended — or just the timestamp
 * when there is no body (GET). `body` must be the exact string sent on the
 * wire; re-serializing it would change the bytes and break the signature.
 */
export function signCxalloyRequest(
  secret: string,
  timestamp: string,
  body?: string
): string {
  return createHmac("sha256", secret)
    .update(`${body ?? ""}${timestamp}`)
    .digest("hex");
}

/**
 * The five headers CxAlloy requires on every request. The timestamp must be
 * the one that was signed; CxAlloy rejects timestamps older than an hour.
 */
export function cxalloyHeaders(
  identifier: string,
  secret: string,
  body?: string,
  now: () => number = Date.now
): Record<string, string> {
  const timestamp = String(Math.floor(now() / 1000));
  return {
    "Content-Type": "application/json",
    Accept: "application/json",
    "cxalloy-identifier": identifier,
    "cxalloy-timestamp": timestamp,
    "cxalloy-signature": signCxalloyRequest(secret, timestamp, body),
    "user-agent": CXALLOY_USER_AGENT,
  };
}

/**
 * CxAlloy reports credential problems as a 400 (unknown key, missing headers)
 * or 403 (bad signature) with a JSON `{ error }` body rather than a 401, so
 * auth failures are recognised by message, not status alone. The phrases are
 * anchored to CxAlloy's own wording so a validation error that merely names a
 * field (e.g. a `timestamp` column) is not mistaken for a bad key.
 */
export function isCxalloyAuthError(status: number, message: string): boolean {
  if (status === 401) {
    return true;
  }
  if (status !== 400 && status !== 403) {
    return false;
  }
  return /api key could not be found|required \d+ headers|signature/i.test(
    message
  );
}

/**
 * CxAlloy's 403 "The timestamp is too old": the signing host's clock is more
 * than an hour off. The key is fine, so this must not tell the user to
 * reconnect.
 */
export function isCxalloyClockSkewError(
  status: number,
  message: string
): boolean {
  return status === 403 && /timestamp is too old/i.test(message);
}

export const CXALLOY_CLOCK_SKEW_MESSAGE =
  "CxAlloy rejected the request timestamp as too old. The Magnus server clock is out of sync with CxAlloy; the API key itself is fine. Try again later.";

async function validateCxalloyAccess(
  secret: string,
  fields?: Record<string, string>
): Promise<ConnectorAccessProbeResult> {
  const identifier = fields?.identifier?.trim();
  if (!identifier) {
    return { ok: false, message: "Missing required field: identifier" };
  }

  const controller = new AbortController();
  const timeout = setTimeout(() => controller.abort(), PROBE_TIMEOUT_MS);

  try {
    const response = await fetch(`${cxalloyApiBase()}/project`, {
      method: "GET",
      headers: cxalloyHeaders(identifier, secret),
      signal: controller.signal,
    });
    if (response.ok) {
      return { ok: true };
    }

    let upstream = "";
    try {
      const body = (await response.json()) as { error?: unknown };
      if (typeof body?.error === "string") {
        upstream = body.error.slice(0, 200);
      }
    } catch {
      // Non-JSON error body: fall through to the status-only message.
    }

    if (isCxalloyClockSkewError(response.status, upstream)) {
      return { ok: false, message: CXALLOY_CLOCK_SKEW_MESSAGE };
    }
    if (isCxalloyAuthError(response.status, upstream)) {
      return {
        ok: false,
        message:
          `CxAlloy rejected this API key${upstream ? ` (${upstream})` : ""}. ` +
          "Check the identifier and secret — the secret is only shown once when " +
          "the key is created, so ask your CxAlloy account admin for a new " +
          "Read-Only key if needed — then reconnect.",
      };
    }
    return {
      ok: false,
      message: `CxAlloy returned status ${response.status}${upstream ? `: ${upstream}` : ""}. Verify the API key and try again.`,
    };
  } catch (err) {
    if (err instanceof Error && err.name === "AbortError") {
      return {
        ok: false,
        message: `CxAlloy did not respond within ${PROBE_TIMEOUT_MS / 1000}s. Try connecting again.`,
      };
    }
    const detail = err instanceof Error ? err.message : String(err);
    return { ok: false, message: `Could not reach CxAlloy: ${detail}` };
  } finally {
    clearTimeout(timeout);
  }
}

export const cxalloyConnector: ConnectorDefinition = {
  id: "cxalloy",
  name: "CxAlloy",
  description:
    "Read CxAlloy TQ commissioning projects — issues, checklists, tests, equipment, and files",
  icon: "cxalloy",
  authType: "apikey",
  kind: "custom",
  apiKey: {
    // The secret signs every request, so it rides the encrypted
    // `accessToken` column; the identifier lands in `metadata.apiKeyFields`.
    bearerField: "secret",
    fields: [
      {
        name: "identifier",
        label: "API key identifier",
        type: "text",
        required: true,
        placeholder: "Paste the API key identifier",
        helpText:
          "The identifier CxAlloy generates alongside the secret — not a name you choose. API keys are created by an account admin in the TQ account settings; ask for a Read-Only key, scoped to your projects if possible.",
      },
      {
        name: "secret",
        label: "API key secret",
        type: "password",
        required: true,
        placeholder: "Paste the API key secret",
        helpText:
          "Shown once when the key is created. Magnus only reads from CxAlloy.",
      },
    ],
  },
  // Each user brings their own key and the host is fixed, so there is no
  // server-side config to gate on.
  isEnabled: () => true,
  validateAccess: validateCxalloyAccess,
};
